import { createTheme } from '@mui/material/styles';
import createPalette from '@mui/material/styles/createPalette';

const palette = createPalette({
    text: {
        primary: '#000000',
        disabled: '#00000061',
    },
    open: {
        main: '#34C759'
    },
    hold: {
        main: '#8E8E93'
    },
    blue: {
        main: '#007AFF',
        heavy: '#007BFF',
        border: '#2E7BFF'
    },
    red: {
        main: '#CF093D',
        leaderboard: '#CF093D',
        compliance: '#CF093D',
    },
    green: {
        main: '#00897C',
        leaderboard: '#2C9A85',
        taskBackground: '#0585781F',
        taskText: '#058578',
    },
    orange: {
        buttons: '#D1914D'
    },
    yellow: {
        inProgress: '#FF9500',
    },
    subtext: {
        main: '#00000099',
        secondary: '#666666'
    },
    background: {
        main: '#F7F9FC',
        white: '#FFFFFF',
        itemSwap: '#EDEDED',
        disabled: '#D6D6D6',
        neutral: '#0000000F',
        positive: '#F0F0F0',
        negative: '#FF3B3021',
        categoryDrawer: '#00000012',
        unreviewedProgress: '#D9D9D9',
        deniedProgress: '#656565',
        customProgress: '#FF6B35',
        darkGray: '#F5F5F5',
        categoryView: '#DADADA',
        selectedRow: '#D9D9D94D',
        card: '#FAFBFC',
        green: '#E8F5E8',
        yellow: '#FFF8E1',
        darkGreen: '#D4EDD4',
        darkYellow: '#FFF3C4',
    },
    itemSwapChip: {
        background: {
            positive: '#25857821',
            negative: '#CF083D1F'
        },
        text: {
            positive: '#258578',
            negative: '#CF083D'
        }
    },
    campaignMonitoring: {
        green: {
            background: '#BBFFDA',
            text: '#00612D',
        },
        yellow: {
            background: '#FFF3C4',
            text: '#7A4F00',
        },
        red: {
            background: '#FFD7D7',
            text: '#980000',
        }
    },
    wasteEstimate: {
        low: {
            background: '#FFFDE7',
            text: '#f57f17',
        },
        medium: {
            background: '#FFF3E0',
            text: '#e65100',
        },
        high: {
            background: '#FFEBEE',
            text: '#d32f2f',
        }
    },
    border: {
        main: '#0000001F',
        green: '#B8D4B8',
        yellow: '#E8C89F',
    },
    privacyPolicy: {
        background: '#EEF4FC',
        border: '#C5D8F5',
        accent: '#1565C0',
    },
    analytics: {
        cardTypes: {
            used: '#1976d2',          // Blue for used
            picked: '#4CAF50',        // Green for picked  
            run: '#4CAF50',           // Green for run
            restock: '#FF9800',       // Orange for restock
            openNotUsed: '#F44336'    // Red for waste/open not used
        },
        forecast: '#a0c2e433',        // Light blue with 20% opacity for forecast
    },
    primary: {
        main: '#007BFF',
    },
    graphColors: {
        blue: '#007AFF',
        cyan: '#00E0FF',
        teal: '#00B8A9',
        green: '#007F3A',
        brightGreen: '#80E24B',
        lime: '#C8E600',
        yellow: '#FFEA00',
        orange: '#FFAA00',
        redOrange: '#FF5500',
        red: '#FF0000',
        magenta: '#B32CC0',
        purple: '#7E3CB8',
        indigo: '#2C2CA5',
        blueGray: '#5A728A',
        nearBlack: '#30343F',
        brown: '#7D5740',
    },
});

const shadows = [
    'none',
    '0px 2px 4px #00000012',
    '0px 3px 6px #00000029',
    '4px 5px 4px #00000012',
    '0px 16px 48px #0000002B',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    'rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px',
    'rgba(0, 0, 0, 0.2) 0px 3px 1px -2px,rgba(0, 0, 0, 0.14) 0px 2px 2px 0px,rgba(0, 0, 0, 0.2) 0px 1px 5px 3px',
    '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
    ...Array(15).fill('none')
];

export const theme = createTheme({
    palette,
    typography: {
        light: {
            'fontFamily': 'Roboto',
            'fontWeight': 300,
            'opacity': 1.0,
        },
        normal: {
            'fontFamily': 'Roboto',
            'fontWeight': 400,
            'opacity': 1.0
        },
        heavy: {
            'fontFamily': 'Roboto',
            'fontWeight': 500,
            'opacity': 1.0
        },
        bold: {
            'fontFamily': 'Roboto',
            'fontWeight': 700,
            'opacity': 1.0
        }
    },
    components: {
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    lineHeight: '1',
                }
            },
            variants: [
                {
                    props: { variant: 'standard' },
                    style: {
                        flex: 1,
                        borderRadius: 4,
                        boxShadow: 'none',
                        border: 'none',
                        fontSize: '14px',
                        padding: '8px',
                        '&.Mui-selected': {
                            color: '#1976d2',
                            background: palette.background.white,
                        }
                    }
                }
            ]
        },
        MuiToggleButtonGroup: {
            variants: [
                {
                    props: { variant: 'standard' },
                    style: {
                        borderRadius: 4,
                        background: '#F0F0F0',
                        padding: '4px',
                    }
                }
            ]
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'capitalize',
                },
                contained: {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    backgroundColor: palette.background.white,
                    color: palette.subtext.main,
                    '&:hover': {
                        backgroundColor: palette.background.white
                    }
                },
                outlined: {
                    borderColor: 'rgb(0, 123, 255, 1.0)'
                },
                blueContained: {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    backgroundColor: palette.blue.main,
                    color: palette.background.white,
                    '&:hover': {
                        backgroundColor: palette.blue.heavy
                    },
                    '&:disabled': {
                        backgroundColor: palette.background.disabled
                    }
                },
                grayContained: {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    backgroundColor: palette.hold.main,
                    color: palette.background.white,
                    '&:hover': {
                        backgroundColor: palette.hold.main
                    },
                    '&:disabled': {
                        backgroundColor: palette.background.disabled
                    }
                },
                orangeContained: {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    backgroundColor: palette.orange.buttons,
                    color: palette.background.white,
                    '&:hover': {
                        backgroundColor: palette.orange.buttons
                    },
                },
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    color: 'black',
                    borderBottom: 'none',
                },
            }
        },
        MuiTableRow: {
            styleOverrides: { 
                root: {
                    '&.Mui-selected': {
                        backgroundColor: palette.background.selectedRow
                    },
                    '&.tableHeadDouble .MuiTableCell-head': {
                        color: 'black',
                        borderBottom: '2px solid rgb(224, 224, 224)',
                        borderTop: '2px solid rgb(224, 224, 224)',
                    },
                    '&.tableFooter .MuiTableCell-head': {
                        color: 'black',
                        borderTop: '2px solid rgb(224, 224, 224)',
                    },
                    '&.tableHeader .MuiTableCell-head': {
                        color: 'black',
                        borderBottom: '2px solid rgb(224, 224, 224)',
                    }
                },     
            }
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root': {
                        '&:last-child': {
                            '& .MuiTableCell-root': {
                                borderBottom: 'none'
                            }
                        },
                        '& .MuiTableCell-root': {
                            borderBottom: '1px solid rgb(224, 224, 224)'
                        }
                    }
                }
            }
        },
        MuiDialog: {
            defaultProps: {
                PaperProps: {
                    elevation: 0
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '18px',
                    fontWeight: '500',
                    opacity: '1.0',
                    fontFamily: 'Roboto',
                    paddingTop: '16px',
                    paddingBottom: '8px',
                }
            }
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    fontWeight: '400',
                    opacity: '1.0',
                    fontFamily: 'Roboto',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    color: 'rgba(0, 0, 0, 1.0)'
                }
            }
        },
        MuiStepConnector:{
            styleOverrides: {
                lineVertical: {
                    padding: '0px',
                    borderLeftWidth: '3px',
                    borderLeftColor: palette.background.white,
                    minHeight: '16px',
                }
            }
        },
        MuiTooltip: {
            variants: [
                {
                    props: { variant: 'white' },
                    style: {},
                },
            ],
            styleOverrides: {
                tooltip: {
                    variants: [
                        {
                            props: { variant: 'white' },
                            style: {
                                backgroundColor: palette.background.white,
                                color: palette.text.primary,
                                boxShadow: shadows[2],
                                border: `1px solid ${palette.border.main}`,
                            },
                        },
                    ],
                },
                arrow: {
                    variants: [
                        {
                            props: { variant: 'white' },
                            style: {
                                color: palette.background.white,
                                '&::before': {
                                    border: `1px solid ${palette.border.main}`,
                                },
                            },
                        },
                    ],
                },
            },
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 450,
            md: 900,
            lg: 1200,
            xl: 1536
        }
    },
    shadows,
});
